import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { Button, Header, Icon, Image, Label, Modal, Segment, Table } from "semantic-ui-react";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { openModal } from "../../../app/common/modals/modalSlice";
import {
  functionGetStrategyEntitlement,
  functionGetStrategyGenerationResult,
  functionGetStrategyPropertiesForTransaction,
} from "../../../app/firestore/functionsService";
import { updateTransactionSettingsInDb } from "../../../app/firestore/firestoreService";
import { convertAddressFull } from "../../../app/common/util/util";
import { buildPropertyKey } from "../../../app/common/util/strategyPropertyKey";
import FormAddress from "../../../app/common/form/FormAddress";
import StrategyPageHeader from "./strategyComponents/StrategyPageHeader";
import BuyerPropertyCard from "./strategyComponents/BuyerPropertyCard";
import CompactBuyerPropertyRow from "./strategyComponents/CompactBuyerPropertyRow";
import { PricingResultPreview } from "./strategyComponents/StrategyResultPreview";
import { buildBuyerStrategyCards, buildPropertyFacts } from "./strategyComponents/buyerStrategyCards";
import { isStrategyVisible } from "./strategyComponents/strategyVisibility";
import { propertyIntelligencePanel } from "./propertyIntelligence/piModel";
import {
  parseDollarNumber,
  formatDollar,
  computePercentDifference,
  HOME_STATUS_LABEL,
  extractKeyConcerns,
  tagifyConcerns,
} from "./strategyComponents/pricingStrategyFormatting";

// "AI recommended offer: $1,150,000 · 4.1% below asking" - a quick read of
// where the AI landed relative to the current list price, without opening
// the full Strategy. Omits the percent clause when there's no asking price
// to compare against (recommendedListPrice is still shown on its own).
function buildOfferSummary(recommendedListPrice, currentPrice) {
  const recommended = parseDollarNumber(recommendedListPrice);
  if (!recommended) return null;
  const asking = parseDollarNumber(currentPrice);
  let percentText = "";
  if (asking) {
    const diffPercent = ((asking - recommended) / asking) * 100;
    if (Math.abs(diffPercent) >= 0.05) {
      percentText = ` · ${Math.abs(diffPercent).toFixed(1)}% ${diffPercent >= 0 ? "below" : "above"} asking`;
    }
  }
  return `AI recommended offer: ${recommendedListPrice}${percentText}`;
}

// A Firestore Timestamp returned through a Cloud Functions onCall response
// serializes as a plain {_seconds, _nanoseconds}-shaped object with no
// .toDate() method - guard before formatting (same pattern used throughout
// the Strategy feature, e.g. StrategyResultViewerModal.jsx).
function toDisplayDate(value) {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : format(date, "MMM d, yyyy");
}


// A new property's address is a plain form, not tied to any existing
// Strategy - reuses the same Formik+FormAddress+Yup shape MissingAddressForm
// (StrategyCreditConfirmModal.jsx) already uses for the same purpose.
function AddPropertyModal({ open, onClose, onSubmit, submitting }) {
  return (
    <Modal open={open} onClose={onClose} size="small" closeIcon>
      <Modal.Content>
        <Segment clearing>
          <Header as="h3" color="blue">Add Property</Header>
          <Formik
            initialValues={{ address: { street: "", unit: "", city: "", state: "", zipcode: "" } }}
            validationSchema={Yup.object({
              address: Yup.object({
                street: Yup.string().required("Street address is required"),
                city: Yup.string().required("City is required"),
                state: Yup.string().required("State is required"),
              }),
            })}
            onSubmit={(values) => onSubmit(values.address)}
          >
            {({ isValid }) => (
              <FormikForm className="ui form">
                <p className="text-grey">
                  Enter the address of the property you're considering. This becomes the transaction's current
                  property, and Strategy Credits already unlocked stay unlocked if you've looked at this address before.
                </p>
                <FormAddress />
                <Button
                  primary
                  type="submit"
                  loading={submitting}
                  disabled={submitting || !isValid}
                  content="Add Property"
                  floated="right"
                  className="small top margin"
                />
                <div className="clear" />
              </FormikForm>
            )}
          </Formik>
        </Segment>
      </Modal.Content>
    </Modal>
  );
}


// Current (or an expanded previous) Buyer property: the compact property
// card with its Property Intelligence panel, plus any other Strategy cards
// that are visible (strategyVisibility.js - all hidden today). An expanded
// previous property renders `bare`, since its row already shows the address.
function BuyerPropertyDashboard({ row, onViewPricing, propertyIntelligence, bare }) {
  const otherCards = buildBuyerStrategyCards({
    hasPricingResult: row.hasPricingResult,
    pricingPreview: row.hasPricingResult ? (
      <PricingResultPreview
        label="Recommended initial offer"
        recommendedPrice={row.recommendedPrice}
        comparePrice={row.currentPrice}
        estimatedValueLow={row.estimatedValueLow}
        estimatedValueHigh={row.estimatedValueHigh}
      />
    ) : null,
    pricingLastUpdated: row.pricingLastUpdated,
    onViewPricing,
    isCurrent: row.isCurrent,
  }).filter((card) => card && isStrategyVisible(card.key));

  return (
    <BuyerPropertyCard
      property={{ ...row.address, pic: row.pic }}
      facts={buildPropertyFacts(row)}
      price={row.currentPrice}
      isCurrent={row.isCurrent}
      panel={propertyIntelligence?.panel}
      panelDateLabel={propertyIntelligence?.dateLabel}
      onPanelAction={propertyIntelligence?.onAction}
      otherCards={otherCards}
      bare={bare}
    />
  );
}

// Small rounded, colored "pill" badge - Negotiation Signals and Key
// Concerns in the Compare Homes table (below) are each rendered as a row of
// these rather than plain text/bullets.
const PILL_STYLE = {
  purple: { background: "#f1ecfc", color: "#6435c9" },
  blue: { background: "#e6f3fc", color: "#2185d0" },
  red: { background: "#fdecea", color: "#c0392b" },
  orange: { background: "#fdf3e3", color: "#b5730a" },
  grey: { background: "#f3f4f5", color: "#767676" },
  green: { background: "#e6f7eb", color: "#1e8e3e" },
};

// `wrap` stacks each pill on its own line (used for Key Concerns, which can
// be a full sentence) instead of flowing multiple short pills inline (used
// for Negotiation Signals/Confidence). Either way the pill's own text is
// always allowed to wrap rather than overflow its fixed-width table column
// (see the table's tableLayout:"fixed" columns below) - table cells don't
// clip overflowing content by default, so a nowrap pill wider than its
// column used to visually spill into the next column, especially at the
// narrower page width when printed. whiteSpace:"normal" fixes that for
// every pill, regardless of mode.
function Pill({ icon, text, color, wrap }) {
  const style = PILL_STYLE[color] || PILL_STYLE.grey;
  return (
    <span
      style={{
        display: wrap ? "flex" : "inline-flex",
        alignItems: wrap ? "flex-start" : "center",
        gap: "4px",
        ...style,
        borderRadius: "12px",
        padding: "3px 9px",
        fontSize: "0.78em",
        fontWeight: 600,
        margin: "2px 4px 2px 0",
        whiteSpace: "normal",
        wordBreak: "break-word",
        width: wrap ? "fit-content" : undefined,
        maxWidth: "100%",
      }}
    >
      {icon && <Icon name={icon} style={{ margin: wrap ? "2px 0 0" : 0, fontSize: "0.95em", flexShrink: 0 }} />}
      {text}
    </span>
  );
}

// Negotiation Signals pills - Days on Market, occupancy status, and list
// price reduction, all read straight from the same data already shown
// elsewhere on that property's Strategy (inputs.timeOnMarket/homeStatus,
// originalListPriceContext) - nothing here is inferred or guessed. "Recent
// listing" is the one derived label (DOM < 14) - it's a deterministic read
// of the same DOM number shown right next to it, not a separate guess.
const RECENT_LISTING_DOM_THRESHOLD = 14;

function buildNegotiationSignals(result) {
  const signals = [];
  const dom = result?.inputs?.timeOnMarket;
  if (dom) {
    const n = parseInt(String(dom).replace(/[^0-9]/g, ""), 10);
    signals.push({ icon: "calendar outline", color: "purple", text: Number.isFinite(n) ? `${n} DOM` : dom });
    if (Number.isFinite(n) && n < RECENT_LISTING_DOM_THRESHOLD) {
      signals.push({ icon: "star outline", color: "purple", text: "Recent listing" });
    }
  }
  const homeStatus = result?.inputs?.homeStatus && result.inputs.homeStatus !== "unknown"
    ? HOME_STATUS_LABEL[result.inputs.homeStatus]
    : null;
  if (homeStatus) signals.push({ icon: "home", color: "blue", text: homeStatus });
  const reductionPercent = result?.originalListPriceContext?.reductionPercent;
  if (Number.isFinite(reductionPercent) && reductionPercent > 0) {
    signals.push({ icon: "arrow down", color: "red", text: `Reduced ${reductionPercent.toFixed(1)}%` });
  }
  return signals;
}

// Compare Homes shows only 3 confidence tiers (High/Moderate/Low) rather
// than the full 5-tier AI scale (High/Moderate-High/Moderate/Low-Moderate/
// Low) used on the full Strategy page - easier to scan across several
// properties at a glance. Purely a display simplification of the same
// underlying confidence value, never a re-judgment of it.
function confidenceDisplay(confidence) {
  if (confidence === "High" || confidence === "Moderate-High") return { text: "High confidence", color: "green" };
  if (confidence === "Low" || confidence === "Low-Moderate") return { text: "Low confidence", color: "red" };
  if (confidence === "Moderate") return { text: "Moderate confidence", color: "orange" };
  return { text: "Confidence unknown", color: "grey" };
}

// Side-by-side comparison of every property in this transaction that has a
// generated Pricing Strategy - a quick way to weigh several homes against
// each other without opening each one's full Strategy. White background /
// gray border (Segment clearing) to match the rest of the Strategy feature
// (Pricing Strategy, Listing Description, etc.) rather than a bare modal.
function CompareHomesModal({ open, onClose, rows, transaction }) {
  const { currentUserProfile } = useSelector((state) => state.profile);

  // Print letterhead (agent's logo, name, brokerage, phone, email) - same
  // fallback order/pattern as StrategyPricingModal.jsx's printed header.
  // Print-only; never shown on screen.
  const letterheadAgentName = `${
    transaction?.agentProfile?.firstName || currentUserProfile?.firstName || ""
  } ${transaction?.agentProfile?.lastName || currentUserProfile?.lastName || ""}`.trim();
  const letterheadBrokerageName = transaction?.agentProfile?.brokerageName || currentUserProfile?.brokerageName || "";
  const letterheadPhone = transaction?.agentProfile?.phone || currentUserProfile?.phone || "";
  const letterheadEmail = transaction?.agentProfile?.email || currentUserProfile?.email || "";
  const letterheadLogoUrl = transaction?.agentProfile?.brokerageLogoRef || currentUserProfile?.brokerageLogoRef || null;

  return (
    <Modal open={open} onClose={onClose} size="fullscreen" closeIcon>
      <Modal.Content>
        <Segment clearing id="compare-homes-print-area">
          <div
            className="print-only"
            style={{ marginBottom: "16px", paddingBottom: "12px", borderBottom: "2px solid #2185d0" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              {letterheadLogoUrl && (
                <img src={letterheadLogoUrl} alt="Brokerage logo" style={{ maxHeight: "60px", maxWidth: "160px" }} />
              )}
              <div>
                {letterheadAgentName && <div style={{ fontSize: "1.1em", fontWeight: 700 }}>{letterheadAgentName}</div>}
                {letterheadBrokerageName && <div style={{ fontSize: "0.95em" }}>{letterheadBrokerageName}</div>}
                {(letterheadPhone || letterheadEmail) && (
                  <div style={{ fontSize: "0.85em", color: "#555" }}>
                    {letterheadPhone}
                    {letterheadPhone && letterheadEmail ? " · " : ""}
                    {letterheadEmail}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", marginBottom: "18px" }}>
            <div
              style={{
                background: "#e8f1fc", borderRadius: "10px", padding: "8px",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}
            >
              <Image src="/assets/TA-Logo-Pro.png" alt="TransActioner Pro" style={{ height: 60, width: "auto" }} />
            </div>
            <div>
              <div style={{ fontSize: "1.4em", fontWeight: 700 }}>Compare Buyer Properties</div>
              <div className="text-grey" style={{ fontSize: "0.92em", marginTop: "2px" }}>
                Compare pricing, negotiation signals, and property concerns across the homes being considered.
              </div>
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="text-grey">
              None of this transaction's properties have a generated Pricing Strategy yet - generate one for at
              least one property to compare it here.
            </p>
          ) : (
            <>
              <p className="text-grey no-print" style={{ fontSize: "0.85em" }}>
                Key Concerns are never guessed - each one is either a fact the agent entered, a deterministic
                price-vs-value comparison, or a factor the AI identified from the information actually given for
                that property (the same factors shown on that property's own Strategy).
              </p>
              <div style={{ overflowX: "auto" }}>
                <Table celled structured compact style={{ tableLayout: "fixed", width: "100%" }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell style={{ width: "16%", whiteSpace: "normal" }}>Property</Table.HeaderCell>
                      <Table.HeaderCell style={{ width: "12%", whiteSpace: "nowrap" }}>Asking Price</Table.HeaderCell>
                      <Table.HeaderCell style={{ width: "13%", whiteSpace: "normal" }}>Recommended Offer</Table.HeaderCell>
                      <Table.HeaderCell style={{ width: "12%", whiteSpace: "normal" }}>Est. Market Value</Table.HeaderCell>
                      <Table.HeaderCell style={{ width: "15%", whiteSpace: "normal" }}>Negotiation Signals</Table.HeaderCell>
                      <Table.HeaderCell style={{ width: "24%", whiteSpace: "normal" }}>Key Concerns</Table.HeaderCell>
                      <Table.HeaderCell className="no-print" style={{ width: "8%", whiteSpace: "normal" }}>View</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {rows.map((row) => {
                      const result = row.result;
                      const concerns = extractKeyConcerns(result);
                      const concernTags = tagifyConcerns(concerns);
                      const signals = buildNegotiationSignals(result);
                      const askingDiff = computePercentDifference(result?.inputs?.currentPrice, result?.recommendedListPrice);
                      const propertyFacts = [
                        result?.inputs?.bedrooms && `${result.inputs.bedrooms} bd`,
                        result?.inputs?.bathrooms && `${result.inputs.bathrooms} ba`,
                        result?.inputs?.squareFeet && `${result.inputs.squareFeet} sqft`,
                      ].filter(Boolean).join(" · ");
                      const homeStatus = result?.inputs?.homeStatus && result.inputs.homeStatus !== "unknown"
                        ? HOME_STATUS_LABEL[result.inputs.homeStatus]
                        : null;
                      const wrapCellStyle = { whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "break-word" };
                      return (
                        <Table.Row key={row.propertyKey}>
                          <Table.Cell style={wrapCellStyle}>
                            <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                              <Image
                                className="no-print compare-homes-thumbnail"
                                src="/assets/placeholder-house.png"
                                style={{ width: 56, height: 42, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
                              />
                              <div>
                                <div>
                                  <b>{row.address?.street}{row.address?.unit ? ` ${row.address.unit}` : ""}</b>
                                  {row.isCurrent && <Label size="tiny" color="blue" style={{ marginLeft: "6px" }}>Current</Label>}
                                </div>
                                <div className="text-grey" style={{ fontSize: "0.85em" }}>
                                  {[row.address?.city, row.address?.state].filter(Boolean).join(", ")}
                                </div>
                                {propertyFacts && (
                                  <div className="text-grey" style={{ fontSize: "0.85em" }}>{propertyFacts}</div>
                                )}
                                {homeStatus && (
                                  <div className="text-grey" style={{ fontSize: "0.85em" }}>{homeStatus}</div>
                                )}
                              </div>
                            </div>
                          </Table.Cell>
                          <Table.Cell style={{ whiteSpace: "nowrap" }}>
                            <div>{formatDollar(result?.inputs?.currentPrice) || <span className="text-grey">Unknown</span>}</div>
                            {result?.originalListPriceContext?.originalListPrice &&
                              Number.isFinite(result.originalListPriceContext.reductionPercent) &&
                              result.originalListPriceContext.reductionPercent > 0 && (
                                <div className="text-grey" style={{ fontSize: "0.78em", marginTop: "2px" }}>
                                  Was {result.originalListPriceContext.originalListPrice}
                                  {" "}(-{result.originalListPriceContext.reductionPercent.toFixed(1)}%)
                                </div>
                            )}
                          </Table.Cell>
                          <Table.Cell style={wrapCellStyle}>
                            <div style={{ fontWeight: 700 }}>{result?.recommendedListPrice || "–"}</div>
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px", marginTop: "2px" }}>
                              <Pill
                                text={confidenceDisplay(result?.confidence).text}
                                color={confidenceDisplay(result?.confidence).color}
                                icon="line graph"
                              />
                              {askingDiff && (
                                <span style={{ fontSize: "0.85em", fontWeight: 600, color: askingDiff.percentDiff <= 0 ? "#1e8e3e" : "#c0392b" }}>
                                  {Math.abs(askingDiff.percentDiff).toFixed(1)}% {askingDiff.percentDiff <= 0 ? "below" : "above"} ask
                                </span>
                              )}
                            </div>
                          </Table.Cell>
                          <Table.Cell style={wrapCellStyle}>
                            {result?.estimatedValueLow && result?.estimatedValueHigh
                              ? `${result.estimatedValueLow} – ${result.estimatedValueHigh}`
                              : <span className="text-grey">Unknown</span>}
                          </Table.Cell>
                          <Table.Cell style={wrapCellStyle}>
                            {signals.length > 0 ? (
                              signals.map((s, i) => <Pill key={i} icon={s.icon} color={s.color} text={s.text} />)
                            ) : (
                              <Pill icon="info circle" color="grey" text="Limited data" />
                            )}
                          </Table.Cell>
                          <Table.Cell style={wrapCellStyle}>
                            {concernTags.length > 0 ? (
                              concernTags.map((tag, i) => <Pill key={i} icon={tag.icon} color={tag.color} text={tag.text} wrap />)
                            ) : (
                              <Pill icon="check circle" color="green" text="None identified" />
                            )}
                          </Table.Cell>
                          <Table.Cell className="no-print" textAlign="center">
                            <Button
                              primary
                              size="small"
                              onClick={() => {
                                onClose();
                                row.onView();
                              }}
                            >
                              View
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              </div>
            </>
          )}
          <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between" }}>
            <Button basic className="no-print" onClick={onClose}>Close</Button>
            <Button basic icon labelPosition="left" className="no-print" onClick={() => window.print()}>
              <Icon name="print" />
              Print
            </Button>
          </div>
        </Segment>
      </Modal.Content>
    </Modal>
  );
}

// Buyer-specific redesign: one row per property (address at top, property
// facts, current list price, then Pricing/Negotiations/Inspection mini-cards
// side by side) instead of the flat current-property-only card grid Seller
// transactions still use - see TransactionStrategyDashboard.jsx, which
// keeps that original layout untouched for Sellers.
export default function BuyerStrategyPropertyRows({
  transaction,
  entitlement,
  existingPricingResult,
  onOpenPricingStrategy,
  onOpenPropertyIntelligence,
  propertyIntelligencePointer,
  strategiesRestricted,
  strategiesRestrictedMessage,
  onGenerated,
  refreshSignal,
  onRefreshNeeded,
}) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const [otherProperties, setOtherProperties] = useState([]);
  const [loadingOtherProperties, setLoadingOtherProperties] = useState(false);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showCompareHomes, setShowCompareHomes] = useState(false);
  const [switching, setSwitching] = useState(false);
  // Which previous properties are expanded to their full dashboard - plain
  // in-component state (reset on remount/navigation), matching this app's
  // existing convention of not persisting expand/collapse state beyond a
  // component's own lifetime unless a page already does so elsewhere.
  const [expandedPropertyKeys, setExpandedPropertyKeys] = useState(() => new Set());

  function toggleExpanded(propertyKey) {
    setExpandedPropertyKeys((prev) => {
      const next = new Set(prev);
      if (next.has(propertyKey)) next.delete(propertyKey);
      else next.add(propertyKey);
      return next;
    });
  }

  const transactionId = transaction?.id;
  // Computed client-side from the transaction's own address (same utility
  // TransactionStrategyPropertyGroups.jsx used) rather than waiting on
  // entitlement.propertyKey to resolve asynchronously - avoids a flash where
  // the current property briefly appears in the "other properties" list
  // before entitlement finishes loading.
  const currentPropertyKey = buildPropertyKey(transaction?.address);

  useEffect(() => {
    if (!transactionId) return;
    let cancelled = false;
    setLoadingOtherProperties(true);
    functionGetStrategyPropertiesForTransaction({ transactionId })
      .then(async (properties) => {
        if (cancelled) return;
        const others = properties.filter((p) => p.propertyKey !== currentPropertyKey);
        const enriched = await Promise.all(
          others.map(async (property) => {
            const pricingEntry = property.strategies?.pricing;
            if (!pricingEntry?.generationId) return { property, result: null };
            try {
              const generation = await functionGetStrategyGenerationResult({
                transactionId,
                generationId: pricingEntry.generationId,
              });
              return {
                property,
                result: generation?.result
                  ? {
                      ...generation.result,
                      generationId: pricingEntry.generationId,
                      propertyKey: property.propertyKey,
                      generatedAt: generation.createdAt,
                    }
                  : null,
              };
            } catch (err) {
              return { property, result: null };
            }
          })
        );
        if (!cancelled) setOtherProperties(enriched);
      })
      .catch(() => {
        if (!cancelled) setOtherProperties([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingOtherProperties(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId, currentPropertyKey, refreshSignal]);

  // Shared by both "+ Add Property" (a brand new address) and "Set as
  // Current Property" (switching back to one already in the list) - mirrors
  // StrategyPricingModal.jsx's handleSwitchToNewProperty exactly, including
  // clearing the single-slot cached research/comps/subject-lookup fields
  // (they're per-TRANSACTION, not per-property, so they're stale for
  // whichever property wasn't the one most recently researched).
  async function switchToProperty(newAddress, existingResultForProperty) {
    setSwitching(true);
    try {
      await updateTransactionSettingsInDb(transaction.id, {
        address: newAddress,
        "strategy.propertyResearch": null,
        "strategy.subjectPropertyLookup": null,
        "strategy.pricingComps": null,
      });
      const entitlementData = await functionGetStrategyEntitlement({ transactionId: transaction.id });
      const updatedTransaction = {
        ...transaction,
        address: newAddress,
        strategy: { ...transaction?.strategy, propertyResearch: null, subjectPropertyLookup: null, pricingComps: null },
      };

      function openPricing(justEnteredPropertyDetails) {
        dispatch(
          openModal({
            modalType: "StrategyPricingModal",
            modalProps: {
              transaction: updatedTransaction,
              existingPricingResult: existingResultForProperty || null,
              onGenerated,
              justEnteredPropertyDetails,
            },
          })
        );
      }

      if (!isStrategyVisible("pricing")) {
        // Pricing is hidden: just switch. The new property's Property
        // Intelligence card (and its own unlock) is on the dashboard.
        toast.success(`Switched to ${newAddress?.street || "the new property"}.`);
      } else if (entitlementData.isPropertyUnlocked) {
        openPricing();
      } else {
        dispatch(
          openModal({
            modalType: "StrategyCreditConfirmModal",
            modalProps: {
              transactionId: transaction.id,
              propertyAddress: newAddress,
              propertyPic: transaction.pic,
              triggerStrategyType: "pricing",
              entitlement: entitlementData,
              onConfirmed: openPricing,
            },
          })
        );
      }
      setShowAddProperty(false);
      if (onRefreshNeeded) onRefreshNeeded();
    } catch (err) {
      toast.error(err?.message || "Unable to switch properties right now.");
    } finally {
      setSwitching(false);
    }
  }

  function handleViewOtherPricing(entry) {
    if (!entry.result) return;
    dispatch(
      openModal({
        modalType: "StrategyResultViewerModal",
        modalProps: {
          transactionId,
          propertyKey: entry.property.propertyKey,
          propertyAddress: entry.property.propertyAddress,
          generationId: entry.property.strategies.pricing.generationId,
          agentRepresents: transaction.agentRepresents,
        },
      })
    );
  }

  // The transaction's own MLS details back up the Strategy inputs (same
  // fields and bathroom formula as the Seller page and StrategyPricingModal).
  const details = transaction?.propertyDetails || {};
  const detailBathrooms = details.bathroomsFull
    ? details.bathroomsFull + (details.bathroomsHalf || 0) * 0.5 + (details.bathroomsThreeQuarter || 0) * 0.75
    : null;
  const currentRow = {
    isCurrent: true,
    propertyKey: currentPropertyKey,
    address: transaction?.address,
    pic: transaction?.pic || null,
    bedrooms: existingPricingResult?.inputs?.bedrooms || details.bedroomsTotal,
    bathrooms: existingPricingResult?.inputs?.bathrooms || detailBathrooms,
    squareFeet: existingPricingResult?.inputs?.squareFeet || details.aboveGradeFinishedSqft,
    lotSizeSqft: details.lotSizeSqft,
    propertyType: existingPricingResult?.inputs?.propertyType,
    yearBuilt: existingPricingResult?.inputs?.yearBuilt,
    currentPrice: existingPricingResult?.inputs?.currentPrice,
    hasPricingResult: Boolean(existingPricingResult),
    recommendedPrice: existingPricingResult?.recommendedListPrice || null,
    estimatedValueLow: existingPricingResult?.estimatedValueLow || null,
    estimatedValueHigh: existingPricingResult?.estimatedValueHigh || null,
    pricingSummary: existingPricingResult
      ? buildOfferSummary(existingPricingResult.recommendedListPrice, existingPricingResult.inputs?.currentPrice)
      : null,
    pricingLastUpdated: toDisplayDate(existingPricingResult?.generatedAt),
  };

  // No photo is persisted per historical property today (confirmed: the
  // strategyProperties record has no pic field) - a previous property's
  // PropertyPhoto always falls back to the neutral placeholder/icon rather
  // than guessing or reusing the current transaction's photo.
  const otherRows = otherProperties.map((entry) => ({
    isCurrent: false,
    propertyKey: entry.property.propertyKey,
    address: entry.property.propertyAddress,
    pic: null,
    bedrooms: entry.result?.inputs?.bedrooms,
    bathrooms: entry.result?.inputs?.bathrooms,
    squareFeet: entry.result?.inputs?.squareFeet,
    propertyType: entry.result?.inputs?.propertyType,
    yearBuilt: entry.result?.inputs?.yearBuilt,
    currentPrice: entry.result?.inputs?.currentPrice,
    hasPricingResult: Boolean(entry.result),
    recommendedPrice: entry.result?.recommendedListPrice || null,
    estimatedValueLow: entry.result?.estimatedValueLow || null,
    estimatedValueHigh: entry.result?.estimatedValueHigh || null,
    pricingSummary: entry.result
      ? buildOfferSummary(entry.result.recommendedListPrice, entry.result.inputs?.currentPrice)
      : null,
    pricingLastUpdated: toDisplayDate(entry.result?.generatedAt),
    _entry: entry,
  }));

  // Compare Homes - only properties that actually have a generated Pricing
  // Strategy can be compared (there's nothing to show otherwise).
  const compareRows = [
    currentRow.hasPricingResult && {
      propertyKey: currentRow.propertyKey,
      address: currentRow.address,
      isCurrent: true,
      result: existingPricingResult,
      onView: () => onOpenPricingStrategy(),
    },
    ...otherRows.filter((row) => row.hasPricingResult).map((row) => ({
      propertyKey: row.propertyKey,
      address: row.address,
      isCurrent: false,
      result: row._entry.result,
      onView: () => handleViewOtherPricing(row._entry),
    })),
  ].filter(Boolean);

  function propertyIntelligenceFor(row) {
    if (!onOpenPropertyIntelligence || !isStrategyVisible("propertyIntelligence")) return null;
    const pointer = row.isCurrent ? propertyIntelligencePointer : row._entry?.property?.strategies?.propertyIntelligence;
    const usablePointer = pointer && pointer.status !== "reset" ? pointer : null;
    const panel = propertyIntelligencePanel({
      pointer: usablePointer,
      isCurrent: row.isCurrent,
      restricted: row.isCurrent && strategiesRestricted && !usablePointer,
      restrictedMessage: strategiesRestrictedMessage,
    });
    return {
      panel,
      dateLabel: toDisplayDate(usablePointer?.completedAt),
      onAction: panel.actionLabel ? () => onOpenPropertyIntelligence(row.isCurrent ? undefined : row.propertyKey) : null,
    };
  }

  const headerActions = (
    <>
      {isStrategyVisible("compareHomes") && (
        <Button basic icon labelPosition="left" onClick={() => setShowCompareHomes(true)}>
          <Icon name="table" />
          Compare Homes
        </Button>
      )}
      <Button primary icon labelPosition="left" onClick={() => setShowAddProperty(true)}>
        <Icon name="plus" />
        Add Property
      </Button>
    </>
  );

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
      <StrategyPageHeader title="Buyer Strategies" icon="home" actions={headerActions} isMobile={isMobile} />

      <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
        {convertAddressFull(transaction?.address) && (
          <BuyerPropertyDashboard row={currentRow} onViewPricing={() => onOpenPricingStrategy()} propertyIntelligence={propertyIntelligenceFor(currentRow)} />
        )}

        {loadingOtherProperties && <p className="text-grey small top margin">Loading other properties...</p>}

        {otherRows.map((row) => (
          <CompactBuyerPropertyRow
            key={row.propertyKey}
            pic={row.pic}
            street={row.address?.street}
            unit={row.address?.unit}
            city={row.address?.city}
            state={row.address?.state}
            facts={[
              row.bedrooms && `${row.bedrooms} bd`,
              row.bathrooms && `${row.bathrooms} ba`,
              row.squareFeet && `${row.squareFeet} sqft`,
            ].filter(Boolean)}
            lastListPrice={row.currentPrice}
            latestOfferSummary={isStrategyVisible("pricing") ? row.pricingSummary : null}
            expanded={expandedPropertyKeys.has(row.propertyKey)}
            onToggle={() => toggleExpanded(row.propertyKey)}
            onSetCurrent={() => switchToProperty(row.address, row._entry.result)}
            isMobile={isMobile}
          >
            <BuyerPropertyDashboard
              row={row}
              onViewPricing={() => handleViewOtherPricing(row._entry)}
              propertyIntelligence={propertyIntelligenceFor(row)}
              bare
            />
          </CompactBuyerPropertyRow>
        ))}
      </div>

      <AddPropertyModal
        open={showAddProperty}
        onClose={() => setShowAddProperty(false)}
        onSubmit={(address) => switchToProperty(address, null)}
        submitting={switching}
      />

      <CompareHomesModal
        open={showCompareHomes && isStrategyVisible("compareHomes")}
        onClose={() => setShowCompareHomes(false)}
        rows={compareRows}
        transaction={transaction}
      />
    </div>
  );
}
